/**
 * Turret + side-hardpoint targeting for the player robot.
 *
 * Extracted from `Robot.ts` as a companion class. `RobotTurretry` owns
 * the main-turret aim state (current angle, target angle, idle sweep
 * oscillation) and the per-side-hardpoint aim state (angle, target
 * angle, enemy / swarm assignments). Methods take the `Robot` as their
 * first argument so they can read chassis pose and dispatch to other
 * subsystems (`robot.weaponry`, LOS checks) without a back-reference.
 *
 * Side hardpoints prefer enemies the main turret isn't already shooting
 * at so a 3- or 4-slot robot actually spreads fire; the `exclude*`
 * lists drive that preference with a fallback to the main target when
 * no alternative exists.
 */

import type { Entity, Enemy } from './Entity';
import type { Robot } from './Robot';
import { wrapAngle } from './mathUtils';
import { TURRET_SWEEP_SPEED, SIDE_HARDPOINT_TURN_SPEED } from './RobotConstants';

/** Construction-time arguments for `RobotTurretry`. */
export interface TurretInit {
  /** Base turret turn speed (rad/s) — scaled by the active stats multiplier. */
  turretTurnSpeed: number;
}

export class RobotTurretry {
  // ── Main turret ──
  turretAngle = 0;
  private turretTargetAngle = 0;
  private turretSweepDir = 1;
  private turretSweepOffset = 0;
  private readonly turretTurnSpeed: number;

  // ── Side hardpoints (always two slots; empty indices ignored) ──
  sideHardpointAngles: number[] = [0, 0];
  private sideHardpointTargetAngles: number[] = [0, 0];
  sideHardpointEnemyTargets: (Enemy | null)[] = [null, null];
  sideHardpointSwarmTargets: number[] = [-1, -1];

  constructor(init: TurretInit) {
    this.turretTurnSpeed = init.turretTurnSpeed;
  }

  /**
   * Aim the main turret. In combat, track the primary target (regular
   * or swarm); otherwise, sweep lazily around the chassis heading.
   * Then rotate toward the target angle at the per-second turn cap.
   */
  updateTurret(robot: Robot, dt: number): void {
    if (robot.state === 'combat' && robot.swarmTargetIdx >= 0 && robot.swarmManager) {
      const sm = robot.swarmManager;
      if (sm.alive[robot.swarmTargetIdx]) {
        const dx = sm.x[robot.swarmTargetIdx]! - robot.x;
        const dy = sm.y[robot.swarmTargetIdx]! - robot.y;
        this.turretTargetAngle = Math.atan2(dy, dx);
      }
    } else if (robot.state === 'combat' && robot.target && robot.target.alive) {
      const dx = robot.target.x - robot.x;
      const dy = robot.target.y - robot.y;
      this.turretTargetAngle = Math.atan2(dy, dx);
    } else {
      // Idle: oscillate around chassis heading between ±0.6 rad.
      this.turretSweepOffset += this.turretSweepDir * TURRET_SWEEP_SPEED * dt;
      if (this.turretSweepOffset > 0.6)  { this.turretSweepOffset =  0.6; this.turretSweepDir = -1; }
      if (this.turretSweepOffset < -0.6) { this.turretSweepOffset = -0.6; this.turretSweepDir =  1; }
      this.turretTargetAngle = robot.angle + this.turretSweepOffset;
    }

    const diff = wrapAngle(this.turretTargetAngle - this.turretAngle);
    const maxTurn = this.turretTurnSpeed * dt;
    this.turretAngle += Math.sign(diff) * Math.min(Math.abs(diff), maxTurn);
    this.turretAngle = wrapAngle(this.turretAngle);
  }

  /**
   * Assign + aim each side hardpoint. Side hardpoints prefer enemies
   * not already claimed by the main turret or other hardpoints so a
   * multi-slot robot spreads fire. When no free target exists they
   * fall back to the main target and otherwise follow turret aim.
   */
  updateSideHardpoints(robot: Robot, dt: number): void {
    const sideIndices = robot.getSideSlotIndices();
    if (sideIndices.length === 0) return;

    const excludeEnemies: Enemy[] = [];
    const excludeSwarmIdx: number[] = [];
    if (robot.target && (robot.target as Enemy).alive) excludeEnemies.push(robot.target as Enemy);
    if (robot.swarmTargetIdx >= 0) excludeSwarmIdx.push(robot.swarmTargetIdx);

    for (let h = 0; h < sideIndices.length; h++) {
      const si = sideIndices[h]!;
      const w = robot.weaponry.weapons[si];

      let hasTarget = false;
      this.sideHardpointEnemyTargets[h] = null;
      this.sideHardpointSwarmTargets[h] = -1;

      if (robot.state === 'combat' && !robot.passive) {
        const range = w ? w.range : robot.detectionRadius;
        const result = this.findSideHardpointTarget(robot, range, excludeEnemies, excludeSwarmIdx);
        if (result) {
          this.sideHardpointTargetAngles[h] = result.angle;
          this.sideHardpointEnemyTargets[h] = result.enemy;
          this.sideHardpointSwarmTargets[h] = result.swarmIdx;
          hasTarget = true;
          if (result.enemy) excludeEnemies.push(result.enemy);
          if (result.swarmIdx >= 0) excludeSwarmIdx.push(result.swarmIdx);
        }
      }

      if (!hasTarget) {
        // No free target — follow main turret aim.
        this.sideHardpointTargetAngles[h] = this.turretAngle;
        if (robot.state === 'combat' && !robot.passive) {
          if (robot.target && (robot.target as Enemy).alive) {
            this.sideHardpointEnemyTargets[h] = robot.target as Enemy;
            this.sideHardpointTargetAngles[h] = Math.atan2(
              (robot.target as Enemy).y - robot.y,
              (robot.target as Enemy).x - robot.x,
            );
          } else if (robot.swarmTargetIdx >= 0 && robot.swarmManager?.alive[robot.swarmTargetIdx]) {
            this.sideHardpointSwarmTargets[h] = robot.swarmTargetIdx;
            const sm = robot.swarmManager;
            this.sideHardpointTargetAngles[h] = Math.atan2(
              sm.y[robot.swarmTargetIdx]! - robot.y,
              sm.x[robot.swarmTargetIdx]! - robot.x,
            );
          }
        }
      }

      const diff = wrapAngle(this.sideHardpointTargetAngles[h]! - this.sideHardpointAngles[h]!);
      const maxTurn = SIDE_HARDPOINT_TURN_SPEED * dt;
      this.sideHardpointAngles[h] = this.sideHardpointAngles[h]! + Math.sign(diff) * Math.min(Math.abs(diff), maxTurn);
      this.sideHardpointAngles[h] = wrapAngle(this.sideHardpointAngles[h]!);
    }
  }

  /**
   * Score the nearest in-range, line-of-sight enemy (regular + swarm)
   * that isn't already in `excludeEnemies` / `excludeSwarmIdx`. Falls
   * back to an excluded target when nothing else is available so
   * multi-slot robots still fire when only one enemy is visible.
   */
  private findSideHardpointTarget(
    robot: Robot,
    range: number,
    excludeEnemies: Enemy[],
    excludeSwarmIdx: number[],
  ): { angle: number; enemy: Enemy | null; swarmIdx: number } | null {
    let bestDist = range;
    let bestEnemy: Enemy | null = null;
    let bestSwarmIdx = -1;
    let bestAngle: number | null = null;

    let fallbackDist = range;
    let fallbackEnemy: Enemy | null = null;
    let fallbackSwarmIdx = -1;
    let fallbackAngle: number | null = null;

    const entities: readonly Entity[] = robot.entities;
    for (const e of entities) {
      if (e.type !== 'enemy' || !e.alive) continue;
      const dx = e.x - robot.x;
      const dy = e.y - robot.y;
      const d = Math.hypot(dx, dy);
      if (d >= range || !robot.hasLineOfSight(e.x, e.y)) continue;

      const excluded = excludeEnemies.includes(e as Enemy);
      if (!excluded && d < bestDist) {
        bestDist = d;
        bestEnemy = e as Enemy;
        bestSwarmIdx = -1;
        bestAngle = Math.atan2(dy, dx);
      } else if (excluded && d < fallbackDist) {
        fallbackDist = d;
        fallbackEnemy = e as Enemy;
        fallbackSwarmIdx = -1;
        fallbackAngle = Math.atan2(dy, dx);
      }
    }

    if (robot.swarmManager && robot.swarmManager.aliveCount > 0) {
      const sm = robot.swarmManager;
      const scanDist = bestAngle !== null ? bestDist : range;
      const idx = sm.findNearest(robot.x, robot.y, scanDist);
      if (idx >= 0 && robot.hasLineOfSight(sm.x[idx]!, sm.y[idx]!)) {
        const excluded = excludeSwarmIdx.includes(idx);
        if (!excluded) {
          bestEnemy = null;
          bestSwarmIdx = idx;
          bestAngle = Math.atan2(sm.y[idx]! - robot.y, sm.x[idx]! - robot.x);
        } else if (fallbackAngle === null) {
          fallbackEnemy = null;
          fallbackSwarmIdx = idx;
          fallbackAngle = Math.atan2(sm.y[idx]! - robot.y, sm.x[idx]! - robot.x);
        }
      }
    }

    if (bestAngle !== null) {
      return { angle: bestAngle, enemy: bestEnemy, swarmIdx: bestSwarmIdx };
    }
    if (fallbackAngle !== null) {
      return { angle: fallbackAngle, enemy: fallbackEnemy, swarmIdx: fallbackSwarmIdx };
    }
    return null;
  }
}
